import axios from "axios";
import moment from "moment";
import { createAsyncThunk, createSlice, createAction } from "@reduxjs/toolkit";
import { getResponse, handleApiError } from "../Handler/ExceptionHandler";
import { baseUrl } from "../../utils/envAccess";


// Backend requires an exact match on Location (no "all locations" wildcard for
// an empty string), so fetching "every appointment" means fetching per known
// clinic location and merging. Keep in sync with LOCATION_OPTIONS in
// ZoomAvailability.jsx and LOCATION_ADDRESSES in clinicianDirectory.js.
// (No "Virtual" entry: availability Location is always a real city -
// ZoomServices.ConvertUtcToPlace needs it to resolve a timezone at booking time.)
const KNOWN_LOCATIONS = ["Calgary", "Edmonton"];

export const getDoctorAppointmentList = createAsyncThunk(
  "doctor/getDoctorAppointmentList",
  async (arg, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    const startDate = arg?.startDate ?? moment().subtract(2, "years").format("YYYY-MM-DD");
    const endDate = arg?.endDate ?? moment().add(1, "year").format("YYYY-MM-DD");
    const locations = arg?.location ? [arg.location] : KNOWN_LOCATIONS;

    try {
      const results = await Promise.allSettled(
        locations.map((location) =>
          axios.post(
            `${baseUrl}Doctor/GetDrappointmentList`,
            { startDate, endDate, location },
            config
          )
        )
      );

      const merged = results
        .filter((r) => r.status === "fulfilled")
        .flatMap((r) => (Array.isArray(r.value.data) ? r.value.data : []));

      if (!merged.length && results.every((r) => r.status === "rejected")) {
        return rejectWithValue(results[0]?.reason?.response?.data);
      }
      return merged;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const patientList = createAsyncThunk(
  "doctor/patientList",
  async ({ page, size }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetPatient/${page}/${size}`,
        config,
      );
      console.log(response, 'responsessss');
      return response;
    } catch (error) {
      handleApiError(error?.response?.data, dispatch, user);
    }
  },
);

export const patientbyDoctor = createAsyncThunk(
  "doctor/GetPatientByDoctor",
  async ({ page = 0, size = 100 }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      // Corrected endpoint without placeholder strings
      const response = await axios.get(
        `${baseUrl}Doctor/GetPatientByDoctor/${page}/${size}`,
        config
      );
      console.log(response, 'responsessss');
      return response;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error?.response?.data, dispatch, user)
      );
    }
  }
);


export const fetchCareGivers = createAsyncThunk(
  "doctor/fetchCareGivers",
  async ({ page, size }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Chat/GetCareGiverList/${page}/${size}`,
        config,
      );
      console.log('Care givers API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Care givers API error:', error);
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getPatientBloodWork = createAsyncThunk(
  "doctor/getBloodWork",
  async ({ patientId, fileType }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const url = `${baseUrl}Doctor/GetPatientDocument/${patientId}/${fileType}`;
      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      handleApiError(error?.response?.data, dispatch, user);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const downloadBloodWork = createAsyncThunk(
  "doctor/downloadBloodWork",
  async (fileRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "*/*",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
      responseType: "blob",
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/DownloadDocument/${fileRef}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Download failed");
    }
  }
);
export const deletePatientBloodWork = createAsyncThunk(
  'doctor/deleteBloodWork',
  async (bloodWorkId, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/DeleteDocument/${bloodWorkId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.obj?.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const linkDoctorToPatient = createAsyncThunk(
  "doctor/linkDoctorToPatient",
  async ({ patientRef, link = true }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/LinkDoctorToPatient`,
        { patientRef, link }, // now uses dynamic value
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error?.response?.data, dispatch, user)
      );
    }
  }
);


export const addPatientBloodWork = createAsyncThunk(
  "doctor/addPatientBloodWork",
  async ({ patientRef, bloodWork }, { rejectWithValue, getState, dispatch }) => {
    console.log(patientRef, bloodWork)
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/AddPatientBloodWork`,
        { patientRef, bloodWork },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const getRequisitionPreview = createAsyncThunk(
  "doctor/getRequisitionPreview",
  async ({ patientRef, requisitionType }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetRequisitionPreview?patientRef=${patientRef}&type=${requisitionType}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const addPatientDocuments = createAsyncThunk(
  "doctor/addPatientDocuments",
  async ({ patientRef, bloodWork }, { rejectWithValue, getState, dispatch }) => {
    console.log("addPatientDocuments Thunk:", { patientRef, bloodWork }); 

    const user = getState()?.authentication?.userAuth; 
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/AddPatientDocument`, 
        { patientRef, document: bloodWork }, 
        config
      );
      console.log("API Response:", response.data); 
      return response.data;

    } catch (error) {
      console.error("API Error:", error); 
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user)); 
    }
  }
);

export const getPatientMed = createAsyncThunk(
  'doctor/getPatientMed',
  async (patientId, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.get(`${baseUrl}Doctor/GetPatientMed/${patientId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.obj?.token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const addPatientMed = createAsyncThunk(
  "doctor/addPatientMed",
  async ({ drugName, dose, amount, route, frequency, strength, duration, refills, Note, patientRef }, { rejectWithValue, getState, dispatch }) => {
    console.log(drugName, dose, amount, route, frequency, strength, duration, refills, Note, patientRef);

    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/AddPatientMed`,
        { drugName, dose, amount, route, frequency, strength, duration, refills, Note, patientRef },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const editPatientMed = createAsyncThunk(
  "doctor/editPatientMed",
  async ({ drugName, dose, amount, route, frequency, id, refills, strength, duration, Note }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    console.log(drugName, dose, amount, route, frequency, id, refills, strength, duration, Note, 'editPatientMed')
    try {
      const response = await axios.patch(
        `${baseUrl}Doctor/EditPatientMed`,
        { drugName, dose, amount, route, frequency, id, refills, strength, duration, Note },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const deletePatientMed = createAsyncThunk(
  "doctor/deletePatientMed",
  async (medId, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/DeletePatientMed/${medId}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const submitAvailability = createAsyncThunk(
  "availability/submitAvailability",
  async (availabilityData, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    console.log(user);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.post(
        `${baseUrl}doctor/setAvailability`,
        availabilityData,
        config,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const getAvailability = createAsyncThunk(
  "availability/getAvailability",
  async ({ month, year }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetAvailability/${month}/${year}`,
        config,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);
export const getPatientAvailability = createAsyncThunk(
  "availability/getAvailability",
  async ({ month, year }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Patient/GetAvailability/${month}/${year}`,
        config,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const getZohoClientID = createAsyncThunk(
  "doctor/getZohoClientID",
  async (_, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;

    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetZohoClientId`,
        config,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);
export const acceptAppointment = createAsyncThunk(
  "doctor/acceptAppointment",
  async ({ appointmentId, status }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;

    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/AcceptAppointment/${appointmentId}/${status}`,
        config
      );
      return response.data; // Return the response data to be used by reducers
    } catch (error) {

      return rejectWithValue(error?.response?.data);
    }
  }
);
export const increaseUserStep = createAsyncThunk(
  "doctor/increaseUserStep",
  async ({ patientId, step }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/MarkPatientStep/${patientId}/${step}`,
        config
      );
      return response.data; // Assuming the API returns JSON data
    } catch (error) {
      handleApiError(error?.response?.data, dispatch, user);
      return rejectWithValue(error?.response?.data || "An error occurred");
    }
  }
);

export const getUpcomingAppointmentForDoctor = createAsyncThunk(
  "doctor/getUpcomingAppointmentForDoctor",
  async (_, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetAllAppointment`,
        config,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const getAvailableDoctorsForDate = createAsyncThunk(
  "availability/getAvailableDoctorsForDate",
  async (date, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Patient/GetAvailableDoctorForDate/${date}`,
        config,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);
export const getDoctorAvailabilityForDay = createAsyncThunk(
  "availability/getDoctorAvailabilityForDay",
  async ({ doctorId, date }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Patient/GetDoctorAvailabilityForDay/${doctorId}/${date}`,
        config,
      );
      return { doctorId, data: response.data }; // Include doctorId in the payload
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);
export const setPatientAppointment = createAsyncThunk(
  "availability/setPatientAppointment",
  async (appointmentId, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth; // Assuming user authentication is stored here
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Patient/SetPatientAppointment/${appointmentId}`,
        config,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const codeGrantAuth = createAsyncThunk(
  "user/codeGrantAuth",
  async ({ appointmentId, code }, { rejectWithValue, getState, dispatch }) => {
    const users = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Authorization: `Bearer ${users?.obj?.token}`,
      },
    };

    try {
      const url = `${baseUrl}Patient/SetPatientAppointment/${appointmentId}/${code}`;
      const response = await axios.get(url, config);
      const responseBack = getResponse(response, dispatch, users);
      return responseBack;
    } catch (error) {
      handleApiError(error?.response?.data, dispatch, users);
    }
  },
);

export const createMeeting = createAsyncThunk(
  "user/createMeeting",
  async ({ appointmentId, code }, { rejectWithValue, getState, dispatch }) => {
    const users = getState()?.authentication?.userAuth;
    const appointmentIdNumber = Number(appointmentId);
    const config = {
      headers: {
        Authorization: `Bearer ${users?.obj?.token}`,
      },
    };

    try {
      const url = `${baseUrl}Doctor/CreateZohoMeeting`;
      //  const locationUrl = new URLSearchParams(code);
      //  const currentLocation = locationUrl.get('location');
      //  console.log('updatedCode',currentLocation)
      //  if(currentLocation == 'in') {
      //    locationUrl.set('location', 'ca'); // Set the location to 'CA' for Canada
      //  }

      //  // Reconstruct the full URL with the updated location
      //  const updatedCode = locationUrl.toString();

      const response = await axios.post(url, { appointmentId: appointmentIdNumber, url: code }, config);
      const responseBack = getResponse(response, dispatch, users);
      return responseBack;
    } catch (error) {
      handleApiError(error?.response?.data, dispatch, users);
    }
  },
);

export const cancelPatientAppointment = createAsyncThunk(
  "availability/cancelPatientAppointment",
  async (appointmentId, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth; // Assuming user authentication is stored here
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Patient/UnSetPatientAppointment/${appointmentId}`,
        config,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const getUpcomingAppointments = createAsyncThunk(
  "doctor/getUpcomingAppointments",
  async (_, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetAllAppointment`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const cancelAppointment = createAsyncThunk(
  "doctor/cancelAppointment",
  async ({ appointmentId, reason }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/CancelAppointment`,
        { AppointmentId: appointmentId, Reason: reason || "" },
        config
      );
      return { appointmentId, data: response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateAvailability = createAsyncThunk(
  "doctor/updateAvailability",
  async ({ start, end, appointID }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/UpdateAvailability`,
        { start, end, appointID },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteAppointment = createAsyncThunk(
  "doctor/deleteAppointment",
  async (appointmentId, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/DeleteAppointment/${appointmentId}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchPatientNotes = createAsyncThunk(
  'doctor/fetchPatientNotes',
  async (patientId, { rejectWithValue,getState }) => {
    const user = getState()?.authentication?.userAuth;

    try {
      const response = await fetch(
        `${baseUrl}Doctor/GetPatientNotes/${patientId}`,
        {
          method: 'GET',
          headers: {
            'accept': 'text/plain',
            Authorization: `Bearer ${user?.obj?.token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch patient notes');
      }
      const data = await response.json();
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addPatientNotes = createAsyncThunk(
  "doctor/addPatientNotes",
  async ({ personalNote, subjective, objective, assessment, patientPlan, appointmentType, isDraft, patientRef }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/AddPatientNotes`,
        { personalNote, subjective, objective, assessment, patientPlan, appointmentType, patientRef, isDraft },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const editPatientNote = createAsyncThunk(
  "doctor/editPatientNote",
  async ({ personalNote, subjective, objective, assessment, patientPlan, noteId, isDraft }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    console.log(personalNote,subjective,objective,patientPlan ,assessment,'lll')
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/EditPatientNote`,
        { personalNote, subjective, objective, assessment, patientPlan, noteId, isDraft },
        config
      );
      return response.data;
    } catch (error) {
      console.log({error})
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const deletePatientNote = createAsyncThunk(
  'doctor/deletePatientNote',
  async (noteId, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    console.log({noteId})
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/DeletePatientNote/${noteId}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const fetchDocumo = createAsyncThunk(
  "doctor/fetchDocumo",
  async (range, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    const { startdate, enddate } = range || {};

    try {
    
      const response = startdate && enddate
        ? await axios.post(
            `${baseUrl}Doctor/FetchDocumo`,
            { startdate, enddate },
            { ...config, headers: { ...config.headers, "Content-Type": "application/json" } }
          )
        : await axios.get(`${baseUrl}Doctor/FetchDocumo`, config);
      console.log('fetchDocumo response:', response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);


export const fetchDocumoForPatient = createAsyncThunk(
  "doctor/fetchDocumoForPatient",
  async (patientRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(`${baseUrl}Doctor/FetchDocumo/${patientRef}`, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const downloadDocumo = createAsyncThunk(
  "doctor/downloadDocumo",
  async (messageNumber, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        Accept: "*/*",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
      responseType: "blob", // Important for handling file downloads
    };

    try {
      const response = await axios.get(
        `${baseUrl}Doctor/DownloadDocumo/${messageNumber}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Download failed");
    }
  }
);

export const searchPatientsForFax = createAsyncThunk(
  'doctor/searchPatientsForFax',
  async ({ firstName = '', lastName = '', uli = '' }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/SearchPatientsForFax`,
        { firstName, lastName, uLI: uli },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.obj?.token}` } }
      );
      return response.data.map((p) => ({
        userRef:   p.userRef   ?? '',
        firstName: p.firstname ?? p.firstName ?? '',
        lastName:  p.lastname  ?? p.lastName  ?? '',
        uli:       p.uli       ?? '',
      }));
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const saveFaxMetadata = createAsyncThunk(
  'doctor/saveFaxMetadata',
  async ({ messageNumber, firstName, lastName, uli }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/SaveFaxMetadata`,
        {
          messageNumber,
          enteredDetails: { firstName, lastName, mrn: uli, phone: '', gender: '', notes: '' },
        },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.obj?.token}` } }
      );
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      dispatch(fetchDocumo(getState()?.doctor?.documoDateRange || undefined));
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const manualMapFax = createAsyncThunk(
  'doctor/manualMapFax',
  async ({ messageNumber, userRef, firstName = '', lastName = '', uli = '' }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/ManualMapFax`,
        {
          messageNumber,
          userRef,
          capturedDetails: { firstName, lastName, mrn: uli, phone: '', gender: '', notes: '' },
        },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.obj?.token}` } }
      );
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      dispatch(fetchDocumo(getState()?.doctor?.documoDateRange || undefined));
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);


export const findPatientUserRefByUli = createAsyncThunk(
  'doctor/findPatientUserRefByUli',
  async ({ uli, patientName }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/SearchPatientsForFax`,
        { firstName: '', lastName: '', uLI: uli },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.obj?.token}` } }
      );
      
      const uliOf = (p) => p?.uli ?? p?.Uli ?? p?.ULI ?? '';
      const userRefOf = (p) => p?.userRef ?? p?.UserRef ?? '';
      const nameOf = (p) => `${p?.firstname ?? p?.firstName ?? ''} ${p?.lastname ?? p?.lastName ?? ''}`
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

      const candidates = (response.data || []).filter((p) => uliOf(p) === uli);

      let resolved = candidates;
      if (candidates.length > 1 && patientName) {
        const targetName = String(patientName).trim().toLowerCase().replace(/\s+/g, ' ');
        const narrowed = candidates.filter((p) => nameOf(p) === targetName);
        if (narrowed.length > 0) resolved = narrowed;
      }

      if (resolved.length === 0) {
        return rejectWithValue({ message: 'Could not find this patient to unassign the fax.' });
      }
      if (resolved.length > 1) {
        return rejectWithValue({
          message: 'Multiple patients share this ULI - cannot safely determine which one to unassign. Please resolve this manually.',
        });
      }

      const userRef = userRefOf(resolved[0]);
      if (!userRef) {
        return rejectWithValue({ message: 'Could not find this patient to unassign the fax.' });
      }
      return userRef;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const unassignFax = createAsyncThunk(
  'doctor/unassignFax',
  async ({ messageNumber, userRef }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.post(
        `${baseUrl}Admin/UnassignFax`,
        { messageNumber, userRef },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.obj?.token}` } }
      );
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      dispatch(fetchDocumo(getState()?.doctor?.documoDateRange || undefined));
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getPatientSupplements = createAsyncThunk(
  'doctor/getPatientSupplements',
  async (patientId, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    try {
      const response = await axios.get(`${baseUrl}Doctor/GetPatientSupplements/${patientId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.obj?.token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addPatientSupplement = createAsyncThunk(
  "doctor/addPatientSupplement",
  async ({ supplementName, dose, metric, amount, amountExtra, route, frequency, notes, patientRef }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Doctor/AddPatientSupplements`,
        { supplementName, dose, metric, amount, amountExtra, route, frequency, notes, patientRef },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const editPatientSupplement = createAsyncThunk(
  "doctor/editPatientSupplement",
  async ({ id, supplementName, dose, metric, amount, amountExtra, route, frequency, notes }, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.patch(
        `${baseUrl}Doctor/EditPatientSupplement`,
        { id, supplementName, dose, metric, amount, amountExtra, route, frequency, notes },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const deletePatientSupplement = createAsyncThunk(
  "doctor/deletePatientSupplement",
  async (supplementId, { rejectWithValue, getState, dispatch }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.delete(
        `${baseUrl}Doctor/DeletePatientSupplement/${supplementId}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error?.response?.data, dispatch, user));
    }
  }
);

export const getMessages = createAsyncThunk(
  "doctor/getMessages",
  async (userRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.get(
        `${baseUrl}Chat/GetMessages/${userRef}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const sendMessage = createAsyncThunk(
  "doctor/sendMessage",
  async ({ userRef, chat, chatRef }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };

    try {
      const response = await axios.post(
        `${baseUrl}Chat/SendMessage`,
        { userRef, chat, chatRef },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getChatHeads = createAsyncThunk(
  "doctor/getChatHeads",
  async (_, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "accept": "text/plain",
        "Authorization": `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Chat/GetChatsHead`,
        config
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getUnreadMessageCount = createAsyncThunk(
  "doctor/getUnreadMessageCount",
  async (_, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "accept": "text/plain",
        "Authorization": `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Chat/GetUnReadMsg`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  "doctor/markMessagesAsRead",
  async (userRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "accept": "text/plain",
        "Authorization": `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.post(
        `${baseUrl}Chat/MarkMessagesAsRead/${userRef}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const markChatsAsRead = createAsyncThunk(
  "doctor/markChatsAsRead",
  async (chatRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "accept": "text/plain",
        "Authorization": `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.post(
        `${baseUrl}Chat/MarkChatsAsRead/${chatRef}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Action creator for optimistic chat read update
export const markChatAsReadOptimistically = createAction('doctor/markChatAsReadOptimistically');

export const deleteChat = createAsyncThunk(
  "doctor/deleteChat",
  async (chatRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    console.log('Delete chat - chatRef:', chatRef);
    console.log('Delete chat - user token:', user?.obj?.token ? 'Present' : 'Missing');
    
    const config = {
      headers: {
        "accept": "text/plain",
        "Authorization": `Bearer ${user?.obj?.token}`,
      },
    };
    
    const url = `${baseUrl}Chat/DeleteMessages/${chatRef}`;
    console.log('Delete chat - API URL:', url);
    
    try {
      const response = await axios.get(url, config);
      console.log('Delete chat - API response:', response);
      return response.data;
    } catch (error) {
      console.error('Delete chat - API error:', error);
      console.error('Delete chat - Error response:', error?.response);
      return rejectWithValue(error?.response?.data);
    }
  }
);

// --- Documents (HCP per-selected-patient "Documents" tab) ---
// "Patient Documents" sub-tab (read-only, patient-uploaded files) reuses the existing
// Doctor/GetPatientDocument endpoint with the same reserved FileType the patient-side
// upload uses (6) — see docs/Backend_Requirements_Documents_*.md.
export const DOCUMENT_FILE_TYPE = 6;

export const getPatientOwnDocuments = createAsyncThunk(
  "doctor/getPatientOwnDocuments",
  async (patientRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetPatientDocument/${patientRef}/${DOCUMENT_FILE_TYPE}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "An error occurred");
    }
  }
);


export const getPatientCareTeamDocuments = createAsyncThunk(
  "doctor/getPatientCareTeamDocuments",
  async (patientRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(
        `${baseUrl}Doctor/GetPatientCareTeamDocuments/${patientRef}`,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Not available yet");
    }
  }
);

export const getPatientCareCredits = createAsyncThunk(
  "doctor/getPatientCareCredits",
  async (patientRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(`${baseUrl}Doctor/GetPatientCareCredits/${patientRef}`, config);
      if (response?.data?.success === false) return rejectWithValue(response.data);
      return response.data?.data ?? [];
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Not available yet");
    }
  }
);

export const getPatientAppointmentHistory = createAsyncThunk(
  "doctor/getPatientAppointmentHistory",
  async (patientRef, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        accept: "text/plain",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.get(`${baseUrl}Doctor/GetPatientAppointmentHistory/${patientRef}`, config);
      if (response?.data?.success === false) return rejectWithValue(response.data);
      return response.data?.data ?? [];
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Not available yet");
    }
  }
);

export const uploadCareTeamDocument = createAsyncThunk(
  "doctor/uploadCareTeamDocument",
  async ({ patientRef, base64, filename, fileTitle, isSharedWithPatient }, { rejectWithValue, getState }) => {
    const user = getState()?.authentication?.userAuth;
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.obj?.token}`,
      },
    };
    try {
      const response = await axios.post(
        `${baseUrl}Doctor/AddPatientDocument`,
        {
          PatientRef: patientRef,
          Document: [{ Base64: base64, Filename: filename, FileTitle: fileTitle, FileType: DOCUMENT_FILE_TYPE }],
          SendFax: false,
          IsSharedWithPatient: isSharedWithPatient,
        },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "An error occurred");
    }
  }
);

const doctorSlices = createSlice({
  name: "doctor",
  initialState: {
    loading: false,
    appErr: null,
    appStatus: null,
    appStatusCode: null,
    serverErr: null,
    patientList: [],
    careGivers: [],
    chatHeads: [],
    upcomingPatientAppointment: [],
    doctorAvailability: {},
    bloodWork: [],
    medStatus: null,
    medError: null,
    medications: [],
    patientNotes: [],
    loadingNotes: false,
    notesError: null,
    editingNote: false,
    editNoteError: null,
    deletingNote: false,
    deleteNoteError: null,
    documoData: [],
    documoLoading: false,
    documoError: null,
    documoDateRange: null,
    patientDocumoData: [],
    patientDocumoLoading: false,
    patientDocumoError: null,
    faxPatientSearchResults: [],
    faxSearchLoading: false,
    faxSearchError: null,
    savingFaxMetadata: false,
    mappingFax: false,
    mappingFaxError: null,
    unassigningFax: false,
    unassigningFaxError: null,
    supplements: [],
    bloodWorkFile1: null,
    bloodWorkFile2: null,
    messages: null,
    chatRef: null,
    chatLoading: false,
    chatError: null,
    unreadMessageCount: 0,
    chatHeadsLoading: false,
    unreadCountLoading: false,
    deleteChatLoading: false,
    deleteChatError: null,
    requisitionPreviewBase64: null,
    requisitionPreviewFilename: null,
    requisitionPreviewLoading: false,
    requisitionPreviewError: null,
  },
  reducers: {
    // Add a reducer for optimistic message updates
    addMessage: (state, action) => {
      if (state.messages?.chats) {
        state.messages.chats.push(action.payload.newMessage);
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(patientList.pending, (state, action) => {
      state.loading = true;
      state.appStatusCode = undefined;
      state.profileAppErr = undefined;
      state.profileServerErr = undefined;
    });
    builder.addCase(patientList.fulfilled, (state, action) => {
      state.loading = false;
      state.appStatus = false;
      state.appStatusCode = undefined;
      state.profileAppErr = undefined;
      state.profileServerErr = undefined;
      state.patientList = action.payload?.data || [];
    });
    builder.addCase(patientList.rejected, (state, action) => {
      state.appErr = action?.payload?.message;
      state.loading = false;
      state.serverErr = undefined;
      state.appStatus = action?.payload?.status;
      state.appStatusCode = action?.payload?.statusCode;
      state.patientList = [];
    });
    builder.addCase(cancelPatientAppointment.pending, (state, action) => {
      state.loading = true;
      state.appErr = null;
      state.serverErr = null;
    });
    builder.addCase(cancelPatientAppointment.fulfilled, (state, action) => {
      state.loading = false;
      state.appStatus = true;
      state.appErr = null;
      state.serverErr = null;
      state.upcomingPatientAppointment = state.upcomingPatientAppointment.filter(
        (appointment) => appointment.appointId !== action.payload
      );
    });
    builder.addCase(cancelPatientAppointment.rejected, (state, action) => {
      state.loading = false;
      state.appErr = action.payload;
      state.serverErr = action.error;
    });
    builder.addCase(cancelAppointment.pending, (state, action) => {
      state.loading = true;
      state.appErr = null;
      state.serverErr = null;
    });
    builder
      .addCase(deletePatientBloodWork.fulfilled, (state, action) => {
        state.bloodWork = state.bloodWork.filter(file => file.fileRef !== action.meta.arg);
      })
      .addCase(deletePatientBloodWork.rejected, (state, action) => {
        state.error = action.payload;
      });
    builder.addCase(cancelAppointment.fulfilled, (state, action) => {
      state.loading = false;
      state.appStatus = true;
      state.appErr = null;
      state.serverErr = null;
      const cancelledId = action.payload?.appointmentId;
      state.zoomAppointments = (state.zoomAppointments || []).filter((r) => {
        const id = r?.appintmentId ?? r?.appointmentId ?? r?.AppintmentId;
        return String(id) !== String(cancelledId);
      });
    });
    builder.addCase(cancelAppointment.rejected, (state, action) => {
      state.loading = false;
      state.appErr = action.payload;
      state.serverErr = action.error;
    });
    builder.addCase(getPatientAvailability.fulfilled, (state, action) => {
      state.appointmentList = action.payload;
      state.error = null;
    });

    builder.addCase(getZohoClientID.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getZohoClientID.fulfilled, (state, action) => {
      state.loading = false;
      state.zohoClientId = action.payload;
    });
    builder.addCase(getZohoClientID.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUpcomingAppointmentForDoctor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getUpcomingAppointmentForDoctor.fulfilled, (state, action) => {
      state.loading = false;
      state.appointmentForDoctor = action.payload;
    });
    builder.addCase(getUpcomingAppointmentForDoctor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAvailableDoctorsForDate.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAvailableDoctorsForDate.fulfilled, (state, action) => {
      state.loading = false;
      state.availableDoctors = action.payload;
    });
    builder.addCase(getAvailableDoctorsForDate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getDoctorAvailabilityForDay.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getDoctorAvailabilityForDay.fulfilled, (state, action) => {
      const { doctorId, data } = action.payload; // Destructure the payload to get doctorId and data

      // Clear the previous data and store the new data
      state.doctorAvailability = { [doctorId]: data };

      state.loading = false;
    });

    builder.addCase(getDoctorAvailabilityForDay.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(setPatientAppointment.fulfilled, (state, action) => {
      state.loading = false;
      state.appointmentConfirmation = action.payload; // Store the response data
      state.error = null;
    });
    builder.addCase(setPatientAppointment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload; // Handle error state
    });
    builder.addCase(setPatientAppointment.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(codeGrantAuth.fulfilled, (state, action) => {
      state.loading = false;
      state.appointmentConfirmation = action.payload;
      state.error = null;
    });
    builder.addCase(codeGrantAuth.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(codeGrantAuth.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder
      .addCase(getPatientBloodWork.pending, (state) => {
        state.bloodWorkStatus = 'loading';
      })
      .addCase(getPatientBloodWork.fulfilled, (state, action) => {
        state.bloodWorkStatus = 'succeeded';
        state.bloodWork = action.payload;
      })
      .addCase(getPatientBloodWork.rejected, (state, action) => {
        state.bloodWorkStatus = 'failed';
        state.bloodWorkError = action.error.message || "Something went wrong";
      });

    builder
      .addCase(getPatientMed.pending, (state) => {
        state.medStatus = 'loading';
      })
      .addCase(getPatientMed.fulfilled, (state, action) => {
        state.medStatus = 'succeeded';
        state.medications = action.payload;
      })
      .addCase(getPatientMed.rejected, (state, action) => {
        state.medStatus = 'failed';
        state.medError = action.error.message || "Something went wrong";
      });



    builder
      .addCase(getUpcomingAppointments.pending, (state) => {
        state.loading = true;
        state.appErr = null;
        state.serverErr = null;
      })
      .addCase(getUpcomingAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.upcomingAppointments = action.payload.records;
      })
      .addCase(getUpcomingAppointments.rejected, (state, action) => {
        state.loading = false;
        state.appErr = action.payload || "Failed to fetch upcoming appointments";
      });

    builder.addCase(createMeeting.fulfilled, (state, action) => {
      state.loading = false;
      state.createMeetingData = action.payload;
      state.error = null;
    });
    builder.addCase(createMeeting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createMeeting.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder
      .addCase(updateAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // You might want to update the availability in your state here
      })
      .addCase(updateAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Remove the deleted appointment from the state
        if (state.upcomingAppointments) {
          state.upcomingAppointments = state.upcomingAppointments.filter(
            appointment => appointment.appointId !== action.meta.arg
          );
        }
      })
      .addCase(deleteAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchPatientNotes.pending, (state) => {
        state.loadingNotes = true;
        state.notesError = null;
      })
      .addCase(fetchPatientNotes.fulfilled, (state, action) => {
        state.loadingNotes = false;
        state.patientNotes = action.payload;
      })
      .addCase(fetchPatientNotes.rejected, (state, action) => {
        state.loadingNotes = false;
        state.notesError = action.payload;
      });

    builder
      .addCase(editPatientNote.pending, (state) => {
        state.editingNote = true;
        state.editNoteError = null;
      })
      .addCase(editPatientNote.fulfilled, (state, action) => {
        state.editingNote = false;
        // Update the note in the patientNotes array
        const updatedNoteIndex = state.patientNotes.findIndex(note => note.noteId === action.payload.noteId);
        if (updatedNoteIndex !== -1) {
          state.patientNotes[updatedNoteIndex] = {
            ...state.patientNotes[updatedNoteIndex],
            ...action.payload
          };
        }
      })
      .addCase(editPatientNote.rejected, (state, action) => {
        state.editingNote = false;
        state.editNoteError = action.payload;
      });

    builder
      .addCase(deletePatientNote.pending, (state) => {
        state.deletingNote = true;
        state.deleteNoteError = null;
      })
      .addCase(deletePatientNote.fulfilled, (state, action) => {
        state.deletingNote = false;
        // Remove the deleted note from the patientNotes array
        state.patientNotes = state.patientNotes.filter(note => note.noteId !== action.meta.arg);
      })
      .addCase(deletePatientNote.rejected, (state, action) => {
        state.deletingNote = false;
        state.deleteNoteError = action.payload;
      });

    builder
      .addCase(getDoctorAppointmentList.pending, (state) => {
        state.zoomAppointmentsLoading = true;
      })
      .addCase(getDoctorAppointmentList.fulfilled, (state, action) => {
        state.zoomAppointmentsLoading = false;
        state.zoomAppointments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getDoctorAppointmentList.rejected, (state, action) => {
        state.zoomAppointmentsLoading = false;
        state.zoomAppointments = [];
        state.zoomAppointmentsError = action.payload;
      });

    builder
      .addCase(fetchDocumo.pending, (state) => {
        state.documoLoading = true;
        state.documoError = null;
      })
      .addCase(fetchDocumo.fulfilled, (state, action) => {
        state.documoLoading = false;
        
        if (action.meta.arg?.startdate && action.meta.arg?.enddate) {
          state.documoDateRange = action.meta.arg;
        }
        const incoming = Array.isArray(action.payload) ? action.payload : [];
        state.documoData = incoming.map(record => {
          const existing = state.documoData.find(r => r.messageNumber === record.messageNumber);
          return {
            ...record,
            patientName: record.patientName || existing?.patientName,
            pendingPatientULI: record.pendingPatientULI || existing?.pendingPatientULI,
          };
        });
        state.documoError = null;
      })
      .addCase(fetchDocumo.rejected, (state, action) => {
        state.documoLoading = false;
        state.documoError = action.payload;
      });

    builder
      .addCase(fetchDocumoForPatient.pending, (state) => {
        state.patientDocumoLoading = true;
        state.patientDocumoError = null;
      })
      .addCase(fetchDocumoForPatient.fulfilled, (state, action) => {
        state.patientDocumoLoading = false;
        state.patientDocumoData = Array.isArray(action.payload) ? action.payload : [];
        state.patientDocumoError = null;
      })
      .addCase(fetchDocumoForPatient.rejected, (state, action) => {
        state.patientDocumoLoading = false;
        state.patientDocumoError = action.payload;
      });

    // Get Patient Supplemegnts
    builder.addCase(getPatientSupplements.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPatientSupplements.fulfilled, (state, action) => {
      state.loading = false;
      state.supplements = action.payload;
    });
    builder.addCase(getPatientSupplements.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Add Patient Supplement
    builder.addCase(addPatientSupplement.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addPatientSupplement.fulfilled, (state, action) => {
      state.loading = false;
      if (state.supplements) {
        state.supplements.push(action.payload);
      } else {
        state.supplements = [action.payload];
      }
    });
    builder.addCase(addPatientSupplement.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Add Care Givers
    builder.addCase(fetchCareGivers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCareGivers.fulfilled, (state, action) => {
      console.log('Care givers reducer payload:', action.payload);
      state.loading = false;
      state.careGivers = action.payload || [];
    });
    builder.addCase(fetchCareGivers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.careGivers = [];
    });

    // Get Messages
    builder.addCase(getMessages.pending, (state) => {
      state.chatLoading = true;
      state.chatError = null;
    });
    builder.addCase(getMessages.fulfilled, (state, action) => {
      state.chatLoading = false;
      state.messages = action.payload;
      state.chatRef = action.payload.chatReference;
      state.chatError = null;
    });
    builder.addCase(getMessages.rejected, (state, action) => {
      state.chatLoading = false;
      state.chatError = action.payload;
    });

    // Send Message
    builder.addCase(sendMessage.pending, (state) => {
      state.chatLoading = true;
      state.chatError = null;
    });
    builder.addCase(sendMessage.fulfilled, (state, action) => {
      state.chatLoading = false;
      state.chatError = null;
      // Message already added optimistically, no need to add again
    });
    builder.addCase(sendMessage.rejected, (state, action) => {
      state.chatLoading = false;
      state.chatError = action.payload;
      // Could add logic here to remove the optimistically added message
    });

    // Get Chat Heads
    builder.addCase(getChatHeads.pending, (state) => {
      state.chatHeadsLoading = true;
      state.error = null;
    });
    builder.addCase(getChatHeads.fulfilled, (state, action) => {
      state.chatHeadsLoading = false;

      state.chatHeads = action.payload.chats || [];
    });
    builder.addCase(getChatHeads.rejected, (state, action) => {
      state.chatHeadsLoading = false;
      state.error = action.payload;
    });

    // Get Unread Message Count
    builder.addCase(getUnreadMessageCount.pending, (state) => {
      state.unreadCountLoading = true;
      state.error = null;
    });
    builder.addCase(getUnreadMessageCount.fulfilled, (state, action) => {
      state.unreadCountLoading = false;
      state.unreadMessageCount = action.payload;
      state.error = null;
    });
    builder.addCase(getUnreadMessageCount.rejected, (state, action) => {
      state.unreadCountLoading = false;
      state.error = action.payload;
    });

    // Mark Messages As Read
    builder.addCase(markMessagesAsRead.pending, (state) => {
      state.unreadCountLoading = true;
      state.error = null;
    });
    builder.addCase(markMessagesAsRead.fulfilled, (state, action) => {
      state.unreadCountLoading = false;
      // The unread count will be refreshed by the getUnreadMessageCount call
      state.error = null;
    });
    builder.addCase(markMessagesAsRead.rejected, (state, action) => {
      state.unreadCountLoading = false;
      state.error = action.payload;
    });

    // Mark Chats As Read
    builder.addCase(markChatsAsRead.pending, (state) => {
      state.unreadCountLoading = true;
      state.error = null;
    });
    builder.addCase(markChatsAsRead.fulfilled, (state, action) => {
      state.unreadCountLoading = false;
      // The unread count will be refreshed by the getUnreadMessageCount call
      state.error = null;
    });
    builder.addCase(markChatsAsRead.rejected, (state, action) => {
      state.unreadCountLoading = false;
      state.error = action.payload;
    });

    // Optimistic update for marking chat as read
    builder.addCase(markChatAsReadOptimistically, (state, action) => {
      const userRef = action.payload;
      // Update the specific chat head to remove new message indicator
      if (state.chatHeads && state.chatHeads.length > 0) {
        const chatHeadIndex = state.chatHeads.findIndex(chat => chat.userRef === userRef);
        if (chatHeadIndex !== -1) {
          state.chatHeads[chatHeadIndex] = {
            ...state.chatHeads[chatHeadIndex],
            newMessage: false
          };
        }
      }
    });

    // Delete Chat
    builder.addCase(deleteChat.pending, (state) => {
      state.deleteChatLoading = true;
      state.deleteChatError = null;
    });
    builder.addCase(deleteChat.fulfilled, (state, action) => {
      state.deleteChatLoading = false;
      state.deleteChatError = null;
      // Refresh chat heads after successful deletion
      // The component will handle refreshing the chat heads
    });
    builder.addCase(deleteChat.rejected, (state, action) => {
      state.deleteChatLoading = false;
      state.deleteChatError = action.payload;
    });

    builder
      .addCase(searchPatientsForFax.pending, (state) => {
        state.faxSearchLoading = true;
        state.faxSearchError = null;
      })
      .addCase(searchPatientsForFax.fulfilled, (state, action) => {
        state.faxSearchLoading = false;
        state.faxPatientSearchResults = action.payload || [];
      })
      .addCase(searchPatientsForFax.rejected, (state, action) => {
        state.faxSearchLoading = false;
        state.faxSearchError = action.payload;
      });

    builder
      .addCase(saveFaxMetadata.pending, (state) => {
        state.savingFaxMetadata = true;
      })
      .addCase(saveFaxMetadata.fulfilled, (state, action) => {
        state.savingFaxMetadata = false;
        const { messageNumber, firstName, lastName, uli } = action.meta.arg;
        const record = state.documoData.find(r => r.messageNumber === messageNumber);
        if (record) {
          record.patientName = `${firstName} ${lastName}`.trim();
          record.pendingPatientULI = uli;
        }
      })
      .addCase(saveFaxMetadata.rejected, (state) => {
        state.savingFaxMetadata = false;
      });

    builder
      .addCase(manualMapFax.pending, (state) => {
        state.mappingFax = true;
        state.mappingFaxError = null;
      })
      .addCase(manualMapFax.fulfilled, (state) => {
        state.mappingFax = false;
      })
      .addCase(manualMapFax.rejected, (state, action) => {
        state.mappingFax = false;
        state.mappingFaxError = action.payload;
      });

    builder
      .addCase(unassignFax.pending, (state) => {
        state.unassigningFax = true;
        state.unassigningFaxError = null;
      })
      .addCase(unassignFax.fulfilled, (state) => {
        state.unassigningFax = false;
      })
      .addCase(unassignFax.rejected, (state, action) => {
        state.unassigningFax = false;
        state.unassigningFaxError = action.payload;
      });

    builder
      .addCase(getRequisitionPreview.pending, (state) => {
        state.requisitionPreviewLoading = true;
        state.requisitionPreviewError = null;
        state.requisitionPreviewBase64 = null;
        state.requisitionPreviewFilename = null;
      })
      .addCase(getRequisitionPreview.fulfilled, (state, action) => {
        state.requisitionPreviewLoading = false;
        state.requisitionPreviewBase64 = action.payload?.base64Pdf ?? null;
        state.requisitionPreviewFilename = action.payload?.filename ?? null;
      })
      .addCase(getRequisitionPreview.rejected, (state, action) => {
        state.requisitionPreviewLoading = false;
        state.requisitionPreviewError = action.payload;
      });
  },
});

export const { addMessage } = doctorSlices.actions;
export default doctorSlices.reducer;
